import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AnyObject,
  DropdownField,
  DynamicArrayField,
  DynamicField,
  DynamicFormOutput,
} from '@via/models';
import {
  CommonService,
  CoreLookupService,
  IAMLookupService,
} from '@via/services';
import { ViaValidators } from '@via/validators';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { KeyFilterModule } from 'primeng/keyfilter';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { ParticipantModel } from 'src/app/layout/modules/case-management/pages/participants-management/participants.model';

import { DirectivesModule } from '../../directives/directives.module';
import { PipesModule } from '../../pipes/pipes.module';
import { OPdfViewerComponent } from '../pdf-viewer/pdf-viewer.component';

@Component({
  selector: 'via-dynamic-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DropdownModule,
    InputTextModule,
    InputNumberModule,
    InputTextareaModule,
    InputSwitchModule,
    ButtonModule,
    CalendarModule,
    MultiSelectModule,
    PipesModule,
    DirectivesModule,
    TooltipModule,
    KeyFilterModule,
    OPdfViewerComponent,
    AccordionModule,
    DividerModule,
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ODynamicFormComponent implements OnChanges {
  // TODO: Handle nested array items
  // TODO: Handle primitive array items
  @Input() formFields: DynamicField[] = [];
  @Input() viewOnly = false;
  @Input() submitButtonText = 'Submit';
  @Input() updateButtonText = 'Update';
  @Output() submitForm = new EventEmitter<DynamicFormOutput>();
  @Output() cancel = new EventEmitter();

  form: FormGroup = new FormGroup({});

  constructor(
    private commonService: CommonService,
    private iamLookupService: IAMLookupService,
    private coreLookupService: CoreLookupService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formFields']) {
      this.formFields = changes['formFields'].currentValue.map(
        (field: DynamicField) =>
          field.inputType === 'select' || field.inputType === 'multi-select'
            ? {
                ...field,
                // TODO: fetch options logic here
              }
            : field
      );
      this.initForm();
    }
  }

  getArrayControls(group: FormGroup, key: string) {
    return (group.controls[key] as FormArray).controls;
  }

  getControl(group: FormGroup, key: string) {
    return group.get(key);
  }

  getSortedFields(formFields: DynamicField[]) {
    const arrayItems = formFields.filter(
      ({ inputType }) =>
        inputType === 'array' || inputType === 'primitive-array'
    );
    const switchItems = formFields.filter(
      ({ inputType }) => inputType === 'switch'
    );
    const staticItems = formFields.filter(
      ({ key }) => key === 'StepStatus' || key === 'Notes'
    );
    const otherItems = formFields.filter(
      ({ key, inputType }) =>
        inputType !== 'array' &&
        inputType !== 'primitive-array' &&
        inputType !== 'switch' &&
        key !== 'StepStatus' &&
        key !== 'Notes'
    );
    return [...otherItems, ...switchItems, ...staticItems, ...arrayItems];
  }

  mapPresetValues(value: AnyObject<any>, fields: DynamicField[]) {
    return fields.map((field) => ({
      ...field,
      value: value[field.jsonPath],
    }));
  }

  addControls(form: FormGroup, formFields: DynamicField[]) {
    for (const element of formFields) {
      let control: AbstractControl;
      let controlKey = element.jsonPath;
      if (element.inputType === 'array') {
        control = new FormArray([]);

        for (const value of element.value || []) {
          const group = new FormGroup({});
          controlKey = element.jsonPath;
          const mappedFields = this.mapPresetValues(
            value,
            element.items.properties
          );
          this.addControls(group, mappedFields);

          group.patchValue(value);
          (control as FormArray).push(group);
        }
      } else if (element.inputType === 'primitive-array') {
        const primControl = new FormControl('');
        control = new FormArray([primControl]);
      } else {
        const value =
          element.inputType === 'date' && element.value
            ? new Date(element.value)
            : element.inputType === 'switch'
              ? !!element.value
              : element.value || (this.viewOnly && '-') || null;
        if (element.inputType == 'date') {
        }
        control = new FormControl(
          {
            value,
            disabled: this.viewOnly,
          },
          this.mapValidators(element)
        );
      }
      form.addControl(controlKey, control);
    }
  }

  addArrayItem(
    group: FormGroup,
    item: DynamicArrayField,
    idx: number,
    event: Event
  ) {
    event.stopPropagation();
    const formArray = group.controls[item.jsonPath] as FormArray;
    const newFormGroup = new FormGroup({});
    this.addControls(newFormGroup, item.items.properties);
    formArray.insert(idx + 1, newFormGroup);
    this.cdr.detectChanges();
  }

  removeArrayItem(
    group: FormGroup,
    item: DynamicArrayField,
    idx: number,
    event: Event
  ) {
    event.stopPropagation();
    const formArray = group.controls[item.jsonPath] as FormArray;
    formArray.removeAt(idx);
    this.cdr.detectChanges();
  }

  visibleFields = new Set<string>();

  initForm() {
    this.form = new FormGroup({});
    this.addControls(this.form, this.formFields);
    this.updateVisibility();
    this.form.valueChanges.subscribe(() => {
      this.updateVisibility();
      this.cdr.markForCheck();
    });
  }

  updateVisibility() {
    const data = this.form.getRawValue();
    this.visibleFields.clear();

    for (const field of this.formFields) {
      const control = this.form.get(field.jsonPath);
      if (!field.visibilityCondition) {
        this.visibleFields.add(field.jsonPath);
        if (!this.viewOnly) control?.enable({ emitEvent: false });
        continue;
      }

      try {
        const keys = Object.keys(data);
        const values = Object.values(data);
        const func = new Function(
          ...keys,
          `return ${field.visibilityCondition}`
        );

        if (func(...values)) {
          this.visibleFields.add(field.jsonPath);
          if (!this.viewOnly) control?.enable({ emitEvent: false });
        } else if (!this.viewOnly) control?.disable({ emitEvent: false });
      } catch (e) {
        this.visibleFields.add(field.jsonPath);
        if (!this.viewOnly) control?.enable({ emitEvent: false });
      }
    }
  }

  isFieldVisible(field: DynamicField): boolean {
    return this.visibleFields.has(field.jsonPath);
  }

  mapValidators(dynamicField: DynamicField) {
    const formValidators = [];
    if (dynamicField.required) formValidators.push(Validators.required);
    if (dynamicField.minLength)
      formValidators.push(Validators.minLength(dynamicField.minLength));
    if (dynamicField.maxLength)
      formValidators.push(Validators.maxLength(dynamicField.maxLength));
    if (dynamicField.inputType === 'date') {
      if (dynamicField.maxVal)
        formValidators.push(
          ViaValidators.maxDateValidator(new Date(dynamicField.maxVal))
        );
      if (dynamicField.minVal)
        formValidators.push(
          ViaValidators.minDateValidator(new Date(dynamicField.minVal))
        );
    } else {
      if (dynamicField.maxVal)
        formValidators.push(Validators.max(Number(dynamicField.maxVal)));
      if (dynamicField.minVal)
        formValidators.push(Validators.min(Number(dynamicField.minVal)));
    }
    if (dynamicField.noOnlyWhiteSpace)
      formValidators.push(ViaValidators.noOnlyWhiteSpaceValidator);
    if (dynamicField.noWhiteSpace)
      formValidators.push(ViaValidators.noWhiteSpaceValidator);
    return formValidators;
  }

  getValidatorErrorMessage([error, errorValue]: [string, any]) {
    let msg = '';

    if (error === 'minlength')
      msg = `doesn't meet the minimum length of ${errorValue?.requiredLength}!`;
    if (error === 'maxlength')
      msg = `exceeds maximum length of ${errorValue?.requiredLength}!`;
    if (error === 'min')
      msg = `doesn't meet minimum value of ${errorValue?.min}!`;
    if (error === 'max') msg = `exceeds maximum value of ${errorValue?.max}!`;
    if (error === 'noOnlyWhiteSpace') msg = 'contains only white spaces!';
    if (error === 'noWhiteSpace') msg = 'contains white spaces!';
    if (error === 'validatePattern')
      msg = "doesn't match provided regular expression!";
    if (error === 'maxDate') msg = 'cannot be after the maximum allowed date!';
    if (error === 'minDate') msg = 'cannot be before the minimum allowed date!';

    return this.getFormInvalidCount() > 1 || error === 'required'
      ? 'Please fill all required values!'
      : `Field ${msg}`;
  }

  getFormInvalidCount() {
    let count = 0;
    for (const control of Object.values(this.form.controls)) {
      if (control.invalid) count++;
    }
    return count;
  }

  save(isUpdate = false) {
    const isValid = this.commonService.validateForm(this.form);
    if (!isValid) {
      for (const control of Object.values(this.form.controls)) {
        if (control.errors) {
          const [firstError] = Object.entries(control.errors);
          this.commonService.showToast(
            'Error',
            this.getValidatorErrorMessage(firstError)
          );
          break;
        }
      }
      return;
    }

    const formData = this.form.getRawValue();
    this.submitForm.emit({
      formData,
      form: this.form,
      formFields: this.formFields,
      isUpdate,
    });
  }

  onCancel() {
    this.cancel.emit();
  }

  // eslint-disable-next-line max-lines-per-function
  getDropdownOptions(field: DropdownField) {
    switch (field.lookupId) {
      case 'lienPriority':
        return computed(() => this.coreLookupService.lienPriorityTypes());
      case 'clients':
        return computed(() => this.iamLookupService.clients());
      case 'lienPriority':
        return computed(() => this.coreLookupService.lienPriorityTypes());
      case 'investors':
        return computed(() => this.coreLookupService.investors());
      case 'insurers':
        return computed(() => this.coreLookupService.insurers());
      case 'mortgageTypes':
        return computed(() => this.coreLookupService.mortgageTypes());
      case 'vendors':
        return computed(() => this.coreLookupService.vendors());
      case 'interestRateAdjustmentCalculationMethodTypes':
        return computed(() => this.coreLookupService.interestTypes());
      case 'paymentFrequencyTypes':
        return computed(() => this.coreLookupService.paymentTypes());
      case 'buildingPermitUsageStandardTypes':
        return computed(() =>
          this.coreLookupService.buildingPermitUsageStandardTypes()
        );
      case 'subTypes':
        return computed(() => this.coreLookupService.subTypes());
      case 'neighborhoodHousingTypes':
        return computed(() => this.coreLookupService.propertyTypes());
      case 'unitOccupancyTypes':
        return computed(() => this.coreLookupService.propertyOccupancyTypes());
      case 'stateCodes':
        return computed(() => this.coreLookupService.states());
      case 'counties':
        return computed(() => []);
      case 'countries':
        return computed(() => this.coreLookupService.countries());
      case 'caseTypes':
        return computed(() => this.coreLookupService.caseTypes());
      case 'sites':
        return computed(() => this.iamLookupService.sites());
      case 'foreclosureTypes':
        return computed(() => this.coreLookupService.foreclosureTypes());
      case 'caseStatuses':
        return computed(() => this.coreLookupService.caseStatuses());
      case 'bankruptcyChapterTypes':
        return computed(() => this.coreLookupService.chapterTypes());
      case 'bankruptcyCourt':
        return computed(() => this.coreLookupService.courts());
      case 'participantTypes':
        return computed(() =>
          Object.values(ParticipantModel).map((item) => ({
            id: item.id,
            name: item.label,
          }))
        );
      case 'participantRoleTypes':
        return computed(() => this.coreLookupService.participantRoles());
      case 'investorTypes':
        return () => this.coreLookupService.investorTypes();
      case 'exceptionStatus':
        return () => [
          { id: 1, name: 'Resolved' },
          { id: 2, name: 'Need 2nd Level Review' },
          { id: 3, name: 'Escalated to Next Level' },
          { id: 4, name: 'Missing Information' },
        ];
      case 'postSolicitationLetterApprovalStatus':
        return () => [
          { id: 1, name: 'Approved' },
          { id: 2, name: 'Sent for corrections' },
        ];
      case 'fdcpaLetterApprovalStatus':
        return () => [
          { id: 1, name: 'Approved' },
          { id: 2, name: 'Sent for corrections' },
        ];
      case 'demandLetterReviewStatus':
        return () => [
          { id: 1, name: 'Approved' },
          { id: 2, name: 'Sent for corrections' },
        ];
      case 'trialPaymentStatus':
        return () => [
          { code: 'PENDING', name: 'Pending' },
          { code: 'COMPLETED', name: 'Completed' },
          { code: 'DELAYED', name: 'Delayed' },
          { code: 'DEFAULTED', name: 'Defaulted' },
        ];
      case 'titleGrade':
        return () => [
          { code: 'A', name: 'A' },
          { code: 'B', name: 'B' },
          { code: 'C', name: 'C' },
        ];
      case 'borrowerDocsQcStatus':
        return () => [
          { code: 'APPROVED', name: 'Approved' },
          { code: 'REJECTED', name: 'Rejected' },
        ];
      case 'lossMitigationStage':
        return () => this.coreLookupService.lossMitigationStage();
      case 'propertyOccupancyStatus':
        return computed(() => this.coreLookupService.propertyOccupancyStatus());
      case 'lienStatus':
        return computed(() => this.coreLookupService.lienStatus());
      case 'lossMitigationStatus':
        return computed(() => [
          {
            code: 'APPROVED',
            name: 'Approved',
          },
          {
            code: 'DENIED',
            name: 'Denied',
          },
          {
            code: 'PLAINTIFFREJECTED',
            name: 'Plaintiff Rejected',
          },
          {
            code: 'INPROGRESS',
            name: 'In Progress',
          },
          {
            code: 'N/A',
            name: 'N/A',
          },
        ]);
      case 'loanStatus':
        return computed(() => this.coreLookupService.loanStatus());
      case 'lossMitigationStatus':
        return computed(() => [
          { code: 'Approved', name: 'Approved' },
          { code: 'Denied', name: 'Denied' },
          { code: 'Plaintiff Rejected', name: 'Plaintiff Rejected' },
          { code: 'In Progress', name: 'In Progress' },
          { code: 'N/A', name: 'N/A' },
        ]);
      case 'lossMitigationProgram':
        return computed(() => [
          { code: 'Repayment Plan', name: 'Repayment Plan' },
          { code: 'Forbearance', name: 'Forbearance' },
          { code: 'Loan Modification', name: 'Loan Modification' },
          { code: 'Short Sale', name: 'Short Sale' },
          { code: 'DIL', name: 'DIL' },
          { code: 'FHA-HAMP', name: 'FHA-HAMP' },
          {
            code: 'Standalone Partial Claim',
            name: 'Standalone Partial Claim',
          },
          { code: 'Other', name: 'Other' },
        ]);
      case 'documentReceiptStatus':
        return computed(() => [
          { code: 'Not Received', name: 'Not Received' },
          { code: 'Partially Received', name: 'Partially Received' },
          { code: 'Complete', name: 'Complete' },
        ]);
      case 'acceptanceStatus':
        return computed(() => [
          { code: 'Accepted', name: 'Accepted' },
          { code: 'Denied', name: 'Denied' },
        ]);
      case 'documentsSentToBorrower':
        return computed(() => [
          { code: 'Yes', name: 'Yes' },
          { code: 'No', name: 'No' },
        ]);
      case 'notInterestedReason':
        return computed(() => [
          { code: 'Rate', name: 'Rate' },
          { code: 'Modification Fee', name: 'Modification Fee' },
          {
            code: 'Pursuing finance with USBHM',
            name: 'Pursuing finance with USBHM',
          },
          { code: 'Refinance', name: 'Refinance' },
          {
            code: 'Pursuing refinance other lender',
            name: 'Pursuing refinance other lender',
          },
          { code: 'Other', name: 'Other' },
        ]);

      case 'status':
        return computed(() => [
          { code: 'Attempted customer', name: 'Attempted customer' },
          {
            code: 'Unable to contact Customer',
            name: 'Unable to contact Customer',
          },
          { code: 'Consent recieved', name: 'Consent recieved' },
          { code: 'Borrower Withdrawn', name: 'Borrower Withdrawn' },
        ]);

      case 'creditReportStatus':
        return computed(() => [
          { code: 'Received', name: 'Received' },
          { code: 'Credit Exception', name: 'Credit Exception' },
        ]);

      case 'creditReportReviewStatus':
        return computed(() => [
          { code: 'Favourable', name: 'Favourable' },
          { code: 'Not favourable', name: 'Not favourable' },
        ]);

      case 'borrowerContactStatus':
        return computed(() => [
          { code: 'Attempted customer', name: 'Attempted customer' },
          { code: 'Contacted customer', name: 'Contacted customer' },
          {
            code: 'Ready to pull the credit report',
            name: 'Ready to pull the credit report',
          },
        ]);

      case 'vOEStatus':
        return computed(() => [
          { code: 'VOE Ordered', name: 'VOE Ordered' },
          { code: 'VOE complete', name: 'VOE complete' },
          { code: 'VOE Exception', name: 'VOE Exception' },
        ]);

      case 'stepStatus':
        return computed(() => [
          { code: 'Borrower Accepts terms', name: 'Borrower Accepts terms' },
          { code: 'Not eligible', name: 'Not eligible' },
          {
            code: 'Unable to contact borrower',
            name: 'Unable to contact borrower',
          },
          {
            code: 'Attempted customer - Eligibility Check',
            name: 'Attempted customer - Eligibility Check',
          },
          {
            code: 'Requested further review',
            name: 'Requested further review',
          },
        ]);

      case 'modDocReadyforReview':
        return computed(() => [
          { code: 'Doc Approved', name: 'Doc Approved' },
          { code: 'Doc Rejected', name: 'Doc Rejected' },
        ]);

      case 'readytoMail':
        return computed(() => [
          { code: 'Sent for E-Sign', name: 'Sent for E-Sign' },
          { code: 'Mailed through UPS', name: 'Mailed through UPS' },
        ]);

      case 'mailedDocs':
        return computed(() => [
          { code: 'Sent for E-Sign', name: 'Sent for E-Sign' },
          { code: 'E-Sign Completed', name: 'E-Sign Completed' },
          { code: 'Mailed through UPS', name: 'Mailed through UPS' },
          { code: 'Doc Executed', name: 'Doc Executed' },
          { code: 'Doc Expired', name: 'Doc Expired' },
        ]);

      case 'docExecuted':
        return computed(() => [
          { code: 'Doc Executed', name: 'Doc Executed' },
          { code: 'Mod Fee Received', name: 'Mod Fee Received' },
          { code: 'Mod Fee Not Received', name: 'Mod Fee Not Received' },
          { code: 'Docs submitted to MIR', name: 'Docs submitted to MIR' },
          { code: 'Doc Expired', name: 'Doc Expired' },
        ]);

      case 'aALetterReadyforReview':
        return computed(() => [
          { code: 'Second Level Review', name: 'Second Level Review' },
          { code: 'Approved', name: 'Approved' },
          { code: 'Rejected', name: 'Rejected' },
          { code: 'Mailed through USPS', name: 'Mailed through USPS' },
          {
            code: 'AA Docs Ready for Upload',
            name: 'AA Docs Ready for Upload',
          },
        ]);

      case 'secondLevelReview':
        return computed(() => [
          { code: 'Approved', name: 'Approved' },
          { code: 'Rejected', name: 'Rejected' },
          { code: 'Mailed through USPS', name: 'Mailed through USPS' },
          {
            code: 'AA Docs Ready for Upload',
            name: 'AA Docs Ready for Upload',
          },
          { code: 'Docs submitted to MIR', name: 'Docs submitted to MIR' },
        ]);

      case 'voeBorrowerContactStatus':
        return computed(() => [
          { code: 'Attemted borrower', name: 'Attemted borrower' },
          { code: 'VOE Complete', name: 'VOE Complete' },
          { code: 'VOE Exception', name: 'VOE Exception' },
        ]);

      case 'voeVendorStatus':
        return computed(() => [
          { code: 'VOE Ordered', name: 'VOE Ordered' },
          { code: 'VOE complete', name: 'VOE complete' },
          {
            code: 'Unable to verify the employment',
            name: 'Unable to verify the employment',
          },
        ]);
    }
    return computed(() => field.options || []);
  }

  // TODO: Remove func once primeNg fixes regex validator
  isRegexValidatedField(item: DynamicField) {
    return item.regex instanceof RegExp;
  }

  parseDate(dateStr: string): Date {
    return new Date(dateStr);
  }
}
